/*
 * PracticePage — Prepare Up's practice workspace.
 *
 * Practice = create a session → work through a mix of activities → review.
 * The page has one way in: the session builder (SessionBuilder), which runs
 * every chosen format interleaved in one session (SessionRunner). Below it,
 * "Your Practice Activity" shows what you've done — finished sessions and
 * recently practised topics — plus the practice streak.
 *
 * Shares the Home page's design system (prepare-up-home.css tokens and atoms,
 * the search, the theme toggle, the goal logic). Every number comes from the
 * engine memory tree, the engine event snapshot or the hub tree — nothing is
 * invented. Case studies are AI-coached scenarios built on the goal's topics
 * (CaseStudyMode).
 */
import React, { useEffect, useMemo, useState } from 'react';
import '../../styles/prepare-up-home.css';
import '../../styles/prepare-up-practice.css';
import APIService from '../../services/APIService';
import { buildPaths, getHubTreeCached } from './paths';
import { Bar, HomeSearch, Loading, ThemeToggle, buildSearchEntries, loadGoal, resolveGoal } from './HomeDashboard';
import { practiceDays, streakFrom, weekFrom } from './progressData';
import SessionBuilder from './SessionBuilder';
import { actOf, loadSessionHistory } from './sessionPlan';
import {
  IconArrowRight, IconBolt, IconBook, IconCalendar, IconCards, IconCheck, IconCode, IconList, IconTarget,
} from './icons';

const pct = (value) => `${Math.round((Number(value) || 0) * 100)}%`;

/* Practice modes — labels for the activity filter. */
const MODE_INFO = {
  mcq: { label: 'MCQ', Icon: IconList, unit: 'questions' },
  theory: { label: 'Theory', Icon: IconBook, unit: 'questions' },
  flashcards: { label: 'Flashcards', Icon: IconCards, unit: 'cards' },
  case: { label: 'Case Studies', short: 'Case Study', Icon: IconTarget, unit: 'scenario' },
  rapid: { label: 'AI Rapid Fire', Icon: IconBolt, unit: 'questions' },   // quick-fire recall (RapidFirePractice)
};
const CORE_MODES = ['mcq', 'theory', 'flashcards', 'case', 'rapid'];

// Engine event surfaces that are practice (interviews live under Interviews).
const SURFACE_LABEL = { mcq: 'MCQ', theory: 'Theory', flashcards: 'Flashcards', rapidfire: 'AI Rapid Fire', dsa: 'Coding' };
const SURFACE_MODE = { mcq: 'mcq', theory: 'theory', flashcards: 'flashcards', rapidfire: 'rapid' };

const ago = (days) => {
  const d = Math.floor(Number(days) || 0);
  if (d <= 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d < 7) return `${d} days ago`;
  if (d < 14) return '1 week ago';
  if (d < 60) return `${Math.floor(d / 7)} weeks ago`;
  return `${Math.floor(d / 30)} months ago`;
};

const daysSince = (iso) => Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 86400000));

export default function PracticePage({
  profile,
  onEditProfile,
  theme,
  onToggleTheme,
  seed,              // optional builder prefill: { topics, types, minutes }
  resumable,         // a paused session, if any
  onResumeSession,
  onDiscardSession,
  onCreateSession,   // (config) → starts the continuous session
  onOpenPath,
  onOpenHubNode,
  onOpenCoding,
}) {
  const [memory, setMemory] = useState(null);
  const [memoryState, setMemoryState] = useState('loading');
  const [snap, setSnap] = useState(null);
  const [hub, setHub] = useState(null);
  const [filter, setFilter] = useState('all');
  const [history] = useState(() => loadSessionHistory());
  const [builderSeed, setBuilderSeed] = useState(seed || null);
  const [builderKey, setBuilderKey] = useState(0);

  useEffect(() => {
    let live = true;
    APIService.getMemoryTree()
      .then((d) => { if (live) { setMemory(d || {}); setMemoryState('ready'); } })
      .catch(() => { if (live) setMemoryState('unavailable'); });
    APIService.getEngineMemory()
      .then((d) => { if (live) setSnap(d || {}); })
      .catch(() => { if (live) setSnap({}); });
    getHubTreeCached()
      .then((d) => { if (live) setHub(d.tree || []); })
      .catch(() => { if (live) setHub([]); });
    return () => { live = false; };
  }, []);

  const memTree = memory?.tree;
  const paths = useMemo(() => (hub ? buildPaths(hub) : null), [hub]);
  const goalOptions = useMemo(() => (paths ? [...paths.roles, ...paths.areas] : []), [paths]);
  const goal = useMemo(() => resolveGoal(goalOptions, loadGoal(), null, memTree), [goalOptions, memTree]);
  const searchEntries = useMemo(() => buildSearchEntries(hub, goalOptions), [hub, goalOptions]);
  const events = useMemo(() => snap?.recent_events || [], [snap]);

  // Coding is offered only when the goal includes DSA.
  const showCoding = !!goal && goal.rootIds.some((id) => id === 'dsa' || id.startsWith('dsa.'));

  /* Practice activity — subtopics with attempts, most recent first. The mode
     is the latest practice event on that subtopic (interview events excluded). */
  const activity = useMemo(() => {
    const rows = [];
    (memTree || []).forEach((root) => (root.children || []).forEach((sub) => {
      if (!(sub.attempts > 0)) return;
      const ev = events
        .filter((e) => SURFACE_LABEL[e.surface] && (e.concept_ids || []).some((cid) => cid === sub.id || String(cid).startsWith(`${sub.id}.`)))
        .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts))[0];
      rows.push({ node: sub, surface: ev?.surface || null });
    }));
    return rows
      .filter((r) => filter === 'all' || SURFACE_MODE[r.surface] === filter)
      .sort((a, b) => (a.node.last_seen_days ?? 1e9) - (b.node.last_seen_days ?? 1e9))
      .slice(0, 6);
  }, [memTree, events, filter]);

  /* Streak — days with practice signals: each topic's last-practised day plus
     the engine's recent events. Counts back from today (or yesterday). */
  const streak = useMemo(() => {
    const days = practiceDays(memTree, events);
    return { count: streakFrom(days), week: weekFrom(days) };
  }, [memTree, events]);

  // "New session" on a topic or a past session refills the builder above.
  const prefill = (next) => {
    setBuilderSeed(next); setBuilderKey((k) => k + 1);
    requestAnimationFrame(() => document.getElementById('ses-title')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };
  const displayName = profile?.display_name || profile?.name || '';

  const modes = [
    { id: 'all', label: 'All' },
    ...CORE_MODES.map((id) => ({ id, label: MODE_INFO[id].label, Icon: MODE_INFO[id].Icon })),
  ];

  return (
    <div className="pu-home pu-practice">
      <div className="pu-page">
        <header className="pu-topbar">
          <HomeSearch entries={searchEntries}
            onPick={(e) => (e.kind === 'track' ? onOpenPath(e.id) : onOpenHubNode?.(e.id))} />
          <div className="pu-topbar-end">
            {onToggleTheme && <ThemeToggle theme={theme} onToggle={onToggleTheme} />}
            <button type="button" className="pu-avatar" onClick={onEditProfile}
              aria-label={displayName ? `${displayName} — profile` : 'Profile'}>
              {profile?.avatar ? <img src={profile.avatar} alt="" /> : <span>{(displayName || 'U').slice(0, 1).toUpperCase()}</span>}
            </button>
          </div>
        </header>

        <SessionBuilder key={builderKey} hub={hub} seed={builderSeed} resumable={resumable}
          onResume={onResumeSession} onDiscard={onDiscardSession} onCreate={onCreateSession} />
        {showCoding && (
          <p className="pp-coding">Preparing for coding rounds? <button type="button" className="pu-link" onClick={onOpenCoding}><IconCode size={14} /> Open coding problems <IconArrowRight size={13} /></button></p>
        )}

        {/* Activity + streak */}
        <div className="pu-split pp-split">
          <section className="pu-panel" aria-labelledby="pp-activity-title">
            <div className="pu-row-head">
              <h2 id="pp-activity-title">Your Practice Activity</h2>
            </div>

            {history.length > 0 && (
              <div className="pp-sessions">
                <h3>Recent sessions</h3>
                <ul>
                  {history.slice(0, 4).map((h) => (
                    <li key={h.id}>
                      <div>
                        <b title={h.scopeLabel}>{h.scopeLabel}</b>
                        <small>
                          {ago(daysSince(h.ts))} · {h.completed} of {h.total} activities
                          {h.mcqAccuracy != null ? ` · ${pct(h.mcqAccuracy)} MCQ accuracy` : ''}
                          {' · '}{(h.types || []).map((t) => actOf(t)?.label).filter(Boolean).join(', ')}
                        </small>
                      </div>
                      <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet"
                        onClick={() => prefill({ topics: h.source === 'topics' ? h.scopeIds.map((id) => ({ id, name: paths?.byId[id]?.name || id })) : [], types: h.types, minutes: h.minutes })}
                        aria-label={`Set up another session like ${h.scopeLabel}`}>
                        Practise again <IconArrowRight size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="pp-topics-head">
              <h3>Recently practised topics</h3>
              <div className="pp-tabs" role="tablist" aria-label="Filter by practice type">
                {modes.map((m) => (
                  <button key={m.id} type="button" role="tab" aria-selected={filter === m.id}
                    className={`pp-tab${filter === m.id ? ' active' : ''}`} onClick={() => setFilter(m.id)}>{m.label}</button>
                ))}
              </div>
            </div>
            {memoryState === 'loading' ? <Loading /> : activity.length ? (
              <table className="pu-table pp-table">
                <thead>
                  <tr><th scope="col">Topic</th><th scope="col">Mode</th><th scope="col">Progress</th><th scope="col">Last practiced</th><th scope="col"><span className="pu-sr">Action</span></th></tr>
                </thead>
                <tbody>
                  {activity.map(({ node, surface }) => {
                    return (
                      <tr key={node.id}>
                        <td data-label="Topic"><b title={node.name}>{node.name}</b></td>
                        <td data-label="Mode"><span className="pu-type">{SURFACE_LABEL[surface] || '—'}</span></td>
                        <td data-label="Progress"><span className="pu-progress"><Bar value={node.mastery} label={`${pct(node.mastery)} held`} /><b>{pct(node.mastery)}</b></span></td>
                        <td data-label="Last practiced"><span className="pu-meta">{ago(node.last_seen_days)}</span></td>
                        <td className="pu-td-action">
                          <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet"
                            onClick={() => prefill({ topics: [{ id: node.id, name: node.name }] })} aria-label={`New session on ${node.name}`}>
                            New session <IconArrowRight size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="pu-empty">
                {filter === 'all' ? 'No practice yet — create your first session above.' : `No recent ${MODE_INFO[filter]?.label || ''} practice.`}
              </p>
            )}
          </section>

          <aside className="pu-panel pp-side" aria-label="Practice streak">
            <section aria-labelledby="pp-streak-title">
              <h2 id="pp-streak-title">Practice Streak</h2>
              <div className="pp-streak">
                <span className="pp-streak-icon" aria-hidden="true"><IconCalendar size={16} /></span>
                <div>
                  <b>{streak.count} {streak.count === 1 ? 'day' : 'days'}</b>
                  <small>{streak.count ? 'Keep going to build momentum.' : 'Practise today to start a streak.'}</small>
                </div>
              </div>
              <ol className="pp-week" aria-label="This week">
                {streak.week.map((d) => (
                  <li key={d.label} className={`${d.done ? 'done' : ''}${d.today ? ' today' : ''}${d.future ? ' future' : ''}`}
                    aria-label={`${d.label}: ${d.done ? 'practised' : d.future ? 'upcoming' : 'no practice'}`}>
                    <span>{d.done && <IconCheck size={12} />}</span>
                    <small>{d.label}</small>
                  </li>
                ))}
              </ol>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
